import { useState, useRef, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

const interestOptions = [
  { id: 'ai-chatbots', label: 'AI Chatbots', icon: 'ri-robot-2-line' },
  { id: 'crm-automation', label: 'CRM Automation', icon: 'ri-contacts-line' },
  { id: 'lead-capture', label: 'Lead Capture', icon: 'ri-user-add-line' },
  { id: 'booking-systems', label: 'Booking Systems', icon: 'ri-calendar-check-line' },
  { id: 'email-automation', label: 'Email Automation', icon: 'ri-mail-send-line' },
  { id: 'whatsapp-automation', label: 'WhatsApp Automation', icon: 'ri-whatsapp-line' },
  { id: 'reviews-reputation', label: 'Reviews & Reputation', icon: 'ri-star-line' },
  { id: 'general-updates', label: 'General Updates', icon: 'ri-notification-3-line' },
];

const industrySizes = [
  'Just Me (Sole Trader)',
  '2–5 Employees',
  '6–15 Employees',
  '16–50 Employees',
  '51–100 Employees',
  '101–250 Employees',
  '251–500 Employees',
  '501–1,000 Employees',
  '1,000+ Employees',
];

const industryTypes = [
  'Hair & Beauty',
  'Dental & Medical',
  'Fitness & Wellness',
  'Builders & Trades',
  'Home Services',
  'Physio & Therapy',
  'Legal & Professional',
  'Hospitality & Events',
  'Retail & eCommerce',
  'Other',
];

interface SignupFormProps {
  activeTab: 'signup' | 'login';
  onTabChange: (tab: 'signup' | 'login') => void;
}

const SignupFormBlock = ({ activeTab, onTabChange }: SignupFormProps) => {
  const navigate = useNavigate();

  // Signup state
  const [interests, setInterests] = useState<string[]>([]);
  const [newsletter, setNewsletter] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [serverError, setServerError] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  // Login state
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginShowPwd, setLoginShowPwd] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState('');

  const toggleInterest = (id: string) => {
    setInterests((prev) => prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]);
  };

  const validate = (form: HTMLFormElement): boolean => {
    const newErrors: Record<string, string> = {};
    const data = new FormData(form);
    if (!data.get('first_name')) newErrors.first_name = 'First name is required';
    if (!data.get('last_name')) newErrors.last_name = 'Last name is required';
    const email = data.get('email') as string;
    if (!email) newErrors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Enter a valid email address';
    const pwd = data.get('password') as string;
    if (!pwd || pwd.length < 8) newErrors.password = 'Password must be at least 8 characters';
    const confirm = data.get('confirm_password') as string;
    if (pwd !== confirm) newErrors.confirm_password = 'Passwords do not match';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formRef.current) return;
    if (!validate(formRef.current)) return;

    setSubmitting(true);
    setServerError('');
    const formData = new FormData(formRef.current);

    const firstName = formData.get('first_name') as string;
    const lastName = formData.get('last_name') as string;
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const businessName = formData.get('business_name') as string;
    const phoneNumber = formData.get('phone_number') as string;
    const industry = formData.get('industry') as string;
    const companySize = formData.get('company_size') as string;

    try {
      // Step 1: Create Supabase Auth user
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
          },
        },
      });

      if (authError) {
        if (authError.message.includes('already registered') || authError.message.includes('already been registered')) {
          setServerError('An account with this email already exists. Please sign in instead.');
        } else {
          setServerError(authError.message || 'Something went wrong. Please try again.');
        }
        setSubmitting(false);
        return;
      }

      if (!authData.user) {
        setServerError('Account creation failed. Please try again.');
        setSubmitting(false);
        return;
      }

      // Step 2: Store profile data + MailerLite sync via edge function
      try {
        const session = authData.session;
        await fetch(`${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/handle-signup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            user_id: authData.user.id,
            first_name: firstName,
            last_name: lastName,
            business_name: businessName || null,
            email,
            phone_number: phoneNumber || null,
            industry: industry || null,
            company_size: companySize || null,
            interests: interests.join(', ') || 'None selected',
            newsletter_opt_in: newsletter,
          }),
        });
      } catch {
        // Profile sync failed - non-critical, user is still created
      }

      // Step 3: Auto sign-in if email confirmation not required
      if (authData.session) {
        navigate('/dashboard');
      } else {
        // Email confirmation required - sign them in manually
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (!signInError) {
          navigate('/dashboard');
        } else {
          // Account created but needs email confirmation
          setServerError('');
          navigate('/dashboard');
        }
      }
    } catch {
      setServerError('Connection error. Please check your internet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogin = async () => {
    if (!loginEmail || !loginPassword) {
      setLoginError('Please enter your email and password.');
      return;
    }
    setLoginLoading(true);
    setLoginError('');
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: loginEmail,
        password: loginPassword,
      });
      if (error) {
        if (error.message.includes('Invalid login credentials') || error.message.includes('invalid_credentials')) {
          setLoginError('Incorrect email or password. Please try again.');
        } else if (error.message.includes('Email not confirmed')) {
          setLoginError('Please confirm your email address before signing in.');
        } else {
          setLoginError(error.message || 'Sign in failed. Please try again.');
        }
      } else {
        navigate('/dashboard');
      }
    } catch {
      setLoginError('Connection error. Please try again.');
    } finally {
      setLoginLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!forgotEmail) {
      setForgotError('Please enter your email address.');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(forgotEmail, {
        redirectTo: `${window.location.origin}/dashboard`,
      });
      if (error) {
        setForgotError(error.message || 'Failed to send reset email. Please try again.');
      } else {
        setForgotSent(true);
      }
    } catch {
      setForgotError('Connection error. Please try again.');
    } finally {
      setForgotLoading(false);
    }
  };

  const inputClass = (field: string) =>
    `w-full bg-ea-black border ${errors[field] ? 'border-red-500/60' : 'border-ea-dark-border'} rounded-lg px-4 py-3 text-white text-sm placeholder-ea-gray/40 focus:outline-none focus:border-ea-blue transition-colors`;

  const labelClass = 'block text-white/80 text-xs font-medium mb-1.5 uppercase tracking-wider';

  return (
    <section id="auth-section" className="bg-ea-black py-20 scroll-mt-20">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left - info panel */}
          <div className="lg:col-span-2">
            <div className="sticky top-28">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-ea-blue/10 border border-ea-blue/30 mb-6">
                <span className="w-1.5 h-1.5 rounded-full bg-ea-blue"></span>
                <span className="text-ea-blue text-xs font-medium uppercase tracking-wider">Free to Join</span>
              </div>
              <h2 className="text-3xl font-bold text-white mb-5 leading-tight">
                Your Account,<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                  Your Automations
                </span>
              </h2>
              <p className="text-ea-gray text-sm leading-relaxed mb-8">
                Join free today. Stay ahead of the curve with every new AI tool, service, and feature we release. Your account is also the gateway to our client portal.
              </p>

              <div className="space-y-4 mb-8">
                {[
                  { icon: 'ri-checkbox-circle-line', text: 'Free forever, no card required' },
                  { icon: 'ri-notification-3-line', text: 'Instant updates on new services' },
                  { icon: 'ri-dashboard-line', text: 'Access your members dashboard instantly' },
                  { icon: 'ri-customer-service-2-line', text: 'Priority support for members' },
                  { icon: 'ri-file-list-3-line', text: 'Exclusive automation tips & resources' },
                ].map((item) => (
                  <div key={item.text} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                      <i className={`${item.icon} text-ea-blue text-sm`}></i>
                    </div>
                    <p className="text-ea-gray text-sm">{item.text}</p>
                  </div>
                ))}
              </div>

              <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-ea-blue/10 border border-ea-blue/20">
                    <i className="ri-layout-grid-line text-ea-blue text-sm"></i>
                  </div>
                  <div>
                    <p className="text-white text-xs font-semibold">Members Dashboard</p>
                    <p className="text-ea-gray/60 text-xs">Instant Access</p>
                  </div>
                  <div className="ml-auto px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30">
                    <span className="text-green-400 text-[10px] font-semibold">LIVE</span>
                  </div>
                </div>
                <p className="text-ea-gray text-xs leading-relaxed">
                  Sign up or log in to access your personal dashboard to track automations, manage your account, and get priority support.
                </p>
              </div>
            </div>
          </div>

          {/* Right - Form */}
          <div className="lg:col-span-3">
            {/* Tab toggle */}
            <div className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-1.5 flex mb-8 w-fit">
              <button
                onClick={() => { onTabChange('signup'); setShowForgot(false); }}
                className={`px-8 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === 'signup' ? 'bg-ea-blue text-white' : 'text-ea-gray hover:text-white'
                }`}
              >
                Create Account
              </button>
              <button
                onClick={() => { onTabChange('login'); setShowForgot(false); }}
                className={`px-8 py-2.5 text-sm font-semibold rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap ${
                  activeTab === 'login' ? 'bg-ea-blue text-white' : 'text-ea-gray hover:text-white'
                }`}
              >
                Sign In
              </button>
            </div>

            {/* Sign In Panel */}
            {activeTab === 'login' && (
              <div className="bg-ea-dark-card border border-ea-dark-border rounded-2xl p-8">
                {showForgot ? (
                  <>
                    <div className="flex items-center gap-3 mb-6">
                      <button
                        onClick={() => { setShowForgot(false); setForgotSent(false); setForgotError(''); setForgotEmail(''); }}
                        className="w-8 h-8 flex items-center justify-center rounded-lg border border-ea-dark-border text-ea-gray hover:text-white hover:border-ea-blue/40 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-left-line text-sm"></i>
                      </button>
                      <div>
                        <h3 className="text-white font-bold text-xl">Reset Password</h3>
                        <p className="text-ea-gray text-xs">We&apos;ll send you a reset link</p>
                      </div>
                    </div>
                    {forgotSent ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-green-500/10 border border-green-500/20 mx-auto mb-5">
                          <i className="ri-mail-check-line text-green-400 text-3xl"></i>
                        </div>
                        <h4 className="text-white font-bold text-lg mb-2">Check Your Inbox</h4>
                        <p className="text-ea-gray text-sm mb-6">
                          We&apos;ve sent a password reset link to <span className="text-white font-medium">{forgotEmail}</span>
                        </p>
                        <button
                          onClick={() => { setShowForgot(false); setForgotSent(false); setForgotEmail(''); }}
                          className="text-ea-blue text-sm hover:underline cursor-pointer"
                        >
                          Back to sign in
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-5">
                        <div>
                          <label className={labelClass}>Email Address</label>
                          <input
                            type="email"
                            value={forgotEmail}
                            onChange={(e) => setForgotEmail(e.target.value)}
                            placeholder="you@company.com"
                            className="w-full bg-ea-black border border-ea-dark-border rounded-lg px-4 py-3 text-white text-sm placeholder-ea-gray/40 focus:outline-none focus:border-ea-blue transition-colors"
                          />
                        </div>
                        {forgotError && (
                          <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-start gap-3">
                            <i className="ri-error-warning-line text-red-400 text-sm mt-0.5"></i>
                            <p className="text-red-400 text-xs">{forgotError}</p>
                          </div>
                        )}
                        <button
                          onClick={handleForgotPassword}
                          disabled={forgotLoading}
                          className="w-full py-3.5 bg-ea-blue hover:bg-ea-blue-light disabled:opacity-60 text-white font-semibold text-sm rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                        >
                          {forgotLoading ? (
                            <><i className="ri-loader-4-line animate-spin"></i> Sending...</>
                          ) : (
                            <><i className="ri-mail-send-line"></i> Send Reset Link</>
                          )}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="text-white font-bold text-xl mb-2">Welcome Back</h3>
                    <p className="text-ea-gray text-sm mb-8">Sign in to your Everything Automation account.</p>
                    <div className="space-y-5">
                      <div>
                        <label className={labelClass}>Email Address</label>
                        <input
                          type="email"
                          value={loginEmail}
                          onChange={(e) => setLoginEmail(e.target.value)}
                          placeholder="you@company.com"
                          className="w-full bg-ea-black border border-ea-dark-border rounded-lg px-4 py-3 text-white text-sm placeholder-ea-gray/40 focus:outline-none focus:border-ea-blue transition-colors"
                          onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Password</label>
                        <div className="relative">
                          <input
                            type={loginShowPwd ? 'text' : 'password'}
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            placeholder="Enter your password"
                            className="w-full bg-ea-black border border-ea-dark-border rounded-lg px-4 py-3 pr-12 text-white text-sm placeholder-ea-gray/40 focus:outline-none focus:border-ea-blue transition-colors"
                            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                          />
                          <button
                            type="button"
                            onClick={() => setLoginShowPwd(!loginShowPwd)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-ea-gray hover:text-white transition-colors cursor-pointer"
                          >
                            <i className={`${loginShowPwd ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={rememberMe}
                            onChange={(e) => setRememberMe(e.target.checked)}
                            className="w-4 h-4 rounded border-ea-dark-border accent-ea-blue"
                          />
                          <span className="text-ea-gray text-xs">Remember me</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => setShowForgot(true)}
                          className="text-ea-blue text-xs hover:underline cursor-pointer whitespace-nowrap"
                        >
                          Forgot password?
                        </button>
                      </div>
                      {loginError && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-start gap-3">
                          <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                            <i className="ri-error-warning-line text-red-400 text-sm"></i>
                          </div>
                          <p className="text-red-400 text-xs leading-relaxed">{loginError}</p>
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={handleLogin}
                        disabled={loginLoading}
                        className="w-full py-3.5 bg-ea-blue hover:bg-ea-blue-light disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold text-sm rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                        style={{ boxShadow: '0 0 20px rgba(14,165,233,0.2)' }}
                      >
                        {loginLoading ? (
                          <><i className="ri-loader-4-line animate-spin text-lg"></i> Signing In...</>
                        ) : (
                          <><i className="ri-login-circle-line"></i> Sign In to Dashboard</>
                        )}
                      </button>
                    </div>
                    <p className="text-ea-gray/60 text-xs text-center mt-6">
                      Don&apos;t have an account?{' '}
                      <button
                        type="button"
                        onClick={() => onTabChange('signup')}
                        className="text-ea-blue hover:underline cursor-pointer"
                      >
                        Create one for free
                      </button>
                    </p>
                  </>
                )}
              </div>
            )}

            {/* Sign Up Panel */}
            {activeTab === 'signup' && (
              <form
                ref={formRef}
                onSubmit={handleSignup}
                data-readdy-form
                className="bg-ea-dark-card border border-ea-dark-border rounded-2xl p-8 space-y-7"
              >
                <div>
                  <h3 className="text-white font-bold text-xl mb-1">Create Your Free Account</h3>
                  <p className="text-ea-gray text-sm">Fill in your details below to get instant dashboard access.</p>
                </div>

                {/* Name row */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>First Name <span className="text-ea-blue">*</span></label>
                    <input name="first_name" type="text" placeholder="John" className={inputClass('first_name')} />
                    {errors.first_name && <p className="text-red-400 text-xs mt-1">{errors.first_name}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Last Name <span className="text-ea-blue">*</span></label>
                    <input name="last_name" type="text" placeholder="Smith" className={inputClass('last_name')} />
                    {errors.last_name && <p className="text-red-400 text-xs mt-1">{errors.last_name}</p>}
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Business Name</label>
                  <input name="business_name" type="text" placeholder="Acme Ltd" className={inputClass('')} />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Email Address <span className="text-ea-blue">*</span></label>
                    <input name="email" type="email" placeholder="you@company.com" className={inputClass('email')} />
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Phone Number</label>
                    <input name="phone_number" type="tel" placeholder="+44 7700 000000" className={inputClass('')} />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Industry / Business Type</label>
                    <div className="relative">
                      <select name="industry" className={`${inputClass('')} appearance-none pr-10 cursor-pointer`} defaultValue="">
                        <option value="" disabled>Select your industry</option>
                        {industryTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                        <i className="ri-arrow-down-s-line text-ea-gray"></i>
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className={labelClass}>Company Size</label>
                    <div className="relative">
                      <select name="company_size" className={`${inputClass('')} appearance-none pr-10 cursor-pointer`} defaultValue="">
                        <option value="" disabled>Select company size</option>
                        {industrySizes.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
                        <i className="ri-arrow-down-s-line text-ea-gray"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>What Are You Interested In?</label>
                  <p className="text-ea-gray/60 text-xs mb-3">Select all that apply and we&apos;ll tailor your updates.</p>
                  <div className="grid grid-cols-2 gap-2.5">
                    {interestOptions.map((opt) => {
                      const selected = interests.includes(opt.id);
                      return (
                        <button key={opt.id} type="button" onClick={() => toggleInterest(opt.id)}
                          className={`flex items-center gap-3 px-4 py-3 rounded-lg border text-left transition-all duration-200 cursor-pointer ${
                            selected ? 'border-ea-blue bg-ea-blue/10 text-white' : 'border-ea-dark-border hover:border-ea-blue/40 text-ea-gray hover:text-white'
                          }`}
                        >
                          <div className="w-4 h-4 flex items-center justify-center shrink-0">
                            <i className={`${opt.icon} text-sm ${selected ? 'text-ea-blue' : 'text-ea-gray'}`}></i>
                          </div>
                          <span className="text-xs font-medium">{opt.label}</span>
                          {selected && (
                            <div className="ml-auto w-4 h-4 flex items-center justify-center shrink-0">
                              <i className="ri-check-line text-ea-blue text-xs"></i>
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className={labelClass}>Password <span className="text-ea-blue">*</span></label>
                    <div className="relative">
                      <input name="password" type={showPassword ? 'text' : 'password'} placeholder="Min. 8 characters" className={`${inputClass('password')} pr-12`} />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-ea-gray hover:text-white transition-colors cursor-pointer">
                        <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                      </button>
                    </div>
                    {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                  </div>
                  <div>
                    <label className={labelClass}>Confirm Password <span className="text-ea-blue">*</span></label>
                    <div className="relative">
                      <input name="confirm_password" type={showConfirm ? 'text' : 'password'} placeholder="Repeat password" className={`${inputClass('confirm_password')} pr-12`} />
                      <button type="button" onClick={() => setShowConfirm(!showConfirm)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-ea-gray hover:text-white transition-colors cursor-pointer">
                        <i className={`${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`}></i>
                      </button>
                    </div>
                    {errors.confirm_password && <p className="text-red-400 text-xs mt-1">{errors.confirm_password}</p>}
                  </div>
                </div>

                <div className="bg-ea-blue/5 border border-ea-blue/20 rounded-xl p-5">
                  <label className="flex items-start gap-4 cursor-pointer">
                    <div className="relative mt-0.5 shrink-0">
                      <div onClick={() => setNewsletter(!newsletter)}
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all duration-200 cursor-pointer ${newsletter ? 'bg-ea-blue border-ea-blue' : 'border-ea-dark-border bg-ea-black'}`}>
                        {newsletter && <i className="ri-check-line text-white text-xs"></i>}
                      </div>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium mb-1">Yes, keep me updated from Everything Automation</p>
                      <p className="text-ea-gray text-xs leading-relaxed">Receive email updates, new service announcements, and exclusive offers. Unsubscribe anytime.</p>
                    </div>
                  </label>
                </div>

                {serverError && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-3 flex items-start gap-3">
                    <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="ri-error-warning-line text-red-400 text-sm"></i>
                    </div>
                    <p className="text-red-400 text-xs leading-relaxed">{serverError}</p>
                  </div>
                )}

                <p className="text-ea-gray/50 text-xs leading-relaxed">
                  By creating an account, you agree to our{' '}
                  <a href="/terms-of-service" className="text-ea-blue hover:underline">Terms of Service</a>{' '}
                  and{' '}
                  <a href="/privacy-policy" className="text-ea-blue hover:underline">Privacy Policy</a>.
                </p>

                <button type="submit" disabled={submitting}
                  className="w-full py-4 bg-ea-blue hover:bg-ea-blue-light disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-sm rounded-lg transition-all duration-300 cursor-pointer whitespace-nowrap flex items-center justify-center gap-3"
                  style={{ boxShadow: '0 0 24px rgba(14,165,233,0.25)' }}>
                  {submitting ? (
                    <><i className="ri-loader-4-line animate-spin text-lg"></i> Creating Account & Signing In...</>
                  ) : (
                    <><i className="ri-user-add-line text-lg"></i> Create Account &amp; Go to Dashboard</>
                  )}
                </button>

                <p className="text-ea-gray/50 text-xs text-center">
                  Already have an account?{' '}
                  <button type="button" onClick={() => onTabChange('login')} className="text-ea-blue hover:underline cursor-pointer">
                    Sign in here
                  </button>
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignupFormBlock;
